/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import Base64 from "./Base64";

export default class BitArray {
    private values: Array<number> = [];
    /*
     * @constructor
     *
     * Creates a new BitArray.
     *
     * @param {number} size
     * @param {Array.<number>=} optData
     */
    constructor(size: number, optData: Array<number>) {
        /**
         * @type {!Array.<number>}
         */
        this.values = optData || new Array(Math.ceil(size / 32));

        if (!optData) {
            for (let i = 0; i < this.values.length; i++) {
                this.values[i] = 0;
            }
        }
    }

    /**
     * Decodes a serialised BitArray.
     *
     * @param {string} str
     * @return {!BitArray}
     */
    public static fromBase64(str: string) {
        const binary = Base64.decode(str);
        const data: number[] = [];

        for (let i = 0; i < binary.length; i += 4) {
            const input = (binary.charCodeAt(i) << 24) | (binary.charCodeAt(i + 1) << 16) | (binary.charCodeAt(i + 2) << 8) | (binary.charCodeAt(i + 3) << 0);
            data.push(input);
        }
        return new BitArray(data.length, data);
    }

    /**
     * Set the bit at position `index` to true.
     *
     * @param {number} index
     */
    public set(index: number) {
        if (Math.floor(index / 32 + 1) > this.values.length) {
            throw new Error("Index is out of bounds.");
        }

        const i = Math.floor(index / 32);
        this.values[i] |= 1 << (index - i * 32);
    }

    /**
     * Retrieve the value of the bit at `index`.
     *
     * @param {number} index
     * @return {boolean}
     */
    public has(index: number) {
        if (Math.floor(index / 32 + 1) > this.values.length) {
            throw new Error("Index is out of bounds.");
        }

        const i = Math.floor(index / 32);
        return !!(this.values[i] & (1 << (index - i * 32)));
    }

    /**
     * Returns the internal representation of the BitArray.
     *
     * @return {Array.<number>}
     */
    public getValues() {
        return this.values;
    }

    /**
     * Returns a string representation of this BitArray (for debugging).
     *
     * @private
     * @return {string}
     */
    public toBitString() {
        return this.values
            .map(v => {
                return ("00000000000000000000000000000000" + (v >>> 0).toString(2)).slice(-32);
            })
            .join("");
    }

    /**
     * Serialises the BitArray to a base64 encoded string.
     *
     * @private
     * @return {string}
     */
    public toBase64() {
        let str = "";

        for (let i = 0; i < this.values.length; i++) {
            const value = this.values[i];

            str +=
                String.fromCharCode((value & 0xff000000) >>> 24) +
                String.fromCharCode((value & 0x00ff0000) >>> 16) +
                String.fromCharCode((value & 0x0000ff00) >>> 8) +
                String.fromCharCode((value & 0x000000ff) >>> 0);
        }

        return Base64.encode(str);
    }
}
